import React from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Box,
  Flex,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Tooltip,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { WarningCircle } from 'phosphor-react';
import { impostoDesc } from './_shared/impostos';
import { useAuditoriaInteligente } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct } from '../format';
import { INotaDrawer } from '../types';
import { UI } from '../ui';

interface NoteDrawerProps {
  empresaId: number;
  tipo: string;
  codigo: string;
  isOpen: boolean;
  onClose: () => void;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Text
      fontSize="10.5px"
      fontWeight={800}
      letterSpacing="1.1px"
      color={UI.brand}
      textTransform="uppercase"
      mb={3}
    >
      {children}
    </Text>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <Flex justify="space-between" gap={3} py={1.5} fontSize="13px">
      <Text color={UI.muted} fontWeight={600}>
        {k}
      </Text>
      <Box color={UI.ink} fontWeight={700} textAlign="right">
        {v}
      </Box>
    </Flex>
  );
}

export function NoteDrawer({ empresaId, tipo, codigo, isOpen, onClose }: NoteDrawerProps) {
  const { getNotaDrawer } = useAuditoriaInteligente();

  const { data, isFetching } = useQuery<INotaDrawer>({
    queryKey: ['ai-nota-drawer', { id: empresaId, tipo, codigo }],
    queryFn: getNotaDrawer,
    enabled: isOpen && !!codigo,
  });

  const cab = data?.cabecalho;
  // Guard: base/imposto por nota vêm inflados da extração (fan-out ~1314x) em
  // ~43% das notas. Quando o imposto supera o valor da nota, a carga é impossível
  // — sinalizamos a inconsistência em vez de exibir um número absurdo.
  const impostoInconsistente =
    !!cab && num(cab.valor_nota) > 0 && num(cab.total_impostos) > num(cab.valor_nota);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <Box p={5} borderBottom="1px solid #E7ECF2">
          <Text fontSize="11px" fontWeight={700} color={UI.muted} letterSpacing=".5px">
            NOTA {cab?.numero_nota ?? '—'} · SÉRIE {cab?.serie ?? '—'} · {cab?.modelo ?? ''}
          </Text>
          <Text fontSize="22px" fontWeight={700} mt={1}>
            {cab ? formatCurrency(cab.valor_nota) : '—'}
          </Text>
        </Box>

        <Box flex={1} overflowY="auto">
          {isFetching && (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          )}

          {!isFetching && data && (
            <>
              <Box p={5} borderBottom="8px solid #F4F7FA">
                <SectionTitle>Dados da nota</SectionTitle>
                <KV k="Data de emissão" v={cab?.data_nota} />
                <KV k="Parceiro" v={cab?.parceiro} />
                <KV k="CNPJ/CPF · UF" v={`${cab?.cnpj_cpf ?? ''} · ${cab?.uf ?? ''}`} />
                <KV k={`Natureza (CFOP ${cab?.cfop ?? ''})`} v={cab?.natureza} />
                <KV k="Acumulador" v={cab?.acumulador} />
                <KV k="Situação" v={cab?.situacao} />
              </Box>

              <Box p={5} borderBottom="8px solid #F4F7FA">
                <SectionTitle>Impostos desta nota</SectionTitle>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th px={2}>Imposto</Th>
                      <Th px={2} isNumeric>
                        Base
                      </Th>
                      <Th px={2} isNumeric>
                        Alíq.
                      </Th>
                      <Th px={2} isNumeric>
                        Valor
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(data.impostos ?? []).map(im => (
                      <Tr key={im.sigla_imposto}>
                        <Td px={2} fontWeight={700}>
                          <Tooltip
                            label={impostoDesc(im.sigla_imposto)}
                            hasArrow
                            placement="top"
                            openDelay={150}
                            fontSize="12px"
                            maxW="280px"
                            borderRadius="8px"
                            px={3}
                            py={2}
                          >
                            <Text
                              as="span"
                              borderBottom="1px dashed"
                              borderColor={UI.faint}
                              cursor="help"
                            >
                              {im.sigla_imposto}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td px={2} isNumeric>
                          {formatCurrency(im.base_calculo)}
                        </Td>
                        <Td px={2} isNumeric>
                          {im.aliquota}%
                        </Td>
                        <Td px={2} isNumeric fontWeight={700}>
                          {formatCurrency(im.valor_imposto)}
                        </Td>
                      </Tr>
                    ))}
                    {(data.impostos ?? []).length === 0 && (
                      <Tr>
                        <Td px={2} colSpan={4} textAlign="center" color={UI.muted}>
                          {data.sem_impostos
                            ? 'Nota só na listagem do Domínio — sem detalhamento de impostos (comum em NFS-e tomada)'
                            : 'Sem impostos destacados'}
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
                <Flex
                  justify="space-between"
                  bg={impostoInconsistente ? UI.negSoft : UI.rowHover}
                  borderRadius="10px"
                  px={3}
                  py={3}
                  mt={3}
                  fontWeight={800}
                  fontSize="14px"
                >
                  <Text>Total de impostos</Text>
                  <Text color={impostoInconsistente ? UI.neg : undefined}>
                    {cab ? formatCurrency(cab.total_impostos) : '—'}
                  </Text>
                </Flex>
                {cab && impostoInconsistente && (
                  <Flex
                    align="flex-start"
                    gap={2}
                    mt={2.5}
                    p={2.5}
                    borderRadius="10px"
                    bg={UI.negSoft}
                  >
                    <Box color={UI.neg} mt="1px" flexShrink={0}>
                      <WarningCircle size={16} weight="fill" />
                    </Box>
                    <Text fontSize="11.5px" color={UI.neg} fontWeight={600}>
                      Imposto inconsistente na origem: o valor destacado supera o total da nota (
                      {formatCurrency(cab.valor_nota)}). A base de cálculo desta nota está inflada
                      na extração — consulte a <b>Apuração</b> para o imposto correto.
                    </Text>
                  </Flex>
                )}
                {cab && !impostoInconsistente && (
                  <Flex justify="space-between" mt={2.5} fontSize="12px" fontWeight={600}>
                    <Text color={UI.muted}>Carga sobre a nota</Text>
                    <Text color={cab.carga_pct > 18 ? UI.neg : UI.pos}>{pct(cab.carga_pct)}</Text>
                  </Flex>
                )}
              </Box>

              <Box p={5} borderBottom="8px solid #F4F7FA">
                <SectionTitle>Integração contábil</SectionTitle>
                {(data.integracao ?? []).length === 0 && (
                  <Text fontSize="13px" color={UI.muted}>
                    Sem dados de integração.
                  </Text>
                )}
                {(data.integracao ?? []).map(it => (
                  <Box key={it.codigo_nota} mb={3}>
                    <KV k="Documento" v={it.documento} />
                    <KV k="Débito" v={`${it.cdeb_acu ?? ''} ${it.descricao_debito ?? ''}`} />
                    <KV k="Crédito" v={`${it.ccre_acu ?? ''} ${it.descricao_credito ?? ''}`} />
                    <KV k="Valor lançado" v={formatCurrency(it.valor_contabil)} />
                    <KV
                      k="Status"
                      v={
                        it.integrada ? (
                          <Badge colorScheme="green">Integrado</Badge>
                        ) : (
                          <Badge colorScheme="red">Pendente</Badge>
                        )
                      }
                    />
                  </Box>
                ))}
              </Box>

              <Box p={5}>
                <SectionTitle>Parcelas / retenções</SectionTitle>
                {(data.parcelas ?? []).length === 0 ? (
                  <Text fontSize="13px" color={UI.muted}>
                    Sem parcelas registradas.
                  </Text>
                ) : (
                  <Text fontSize="13px" color={UI.muted}>
                    {(data.parcelas ?? []).length} parcela(s).
                  </Text>
                )}
              </Box>
            </>
          )}
        </Box>
      </DrawerContent>
    </Drawer>
  );
}
